// Header.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase'; 
import { useAuth } from '../contexts/AuthContext';
import Tooltip from "./Tooltip";
import { FaBars, FaTimes, FaAngleDown, FaVideo } from "react-icons/fa";
import packageJson from '../../package.json';
import '../App.css';

const APP_VERSION = packageJson.version || '0.0.0';
/** Public folder; use PUBLIC_URL so subpath deploys and dev server both resolve correctly */
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/logo_shokiq.svg`;

function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      setUserData(null);
      return;
    }
    const docRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        setUserData(snapshot.exists() ? snapshot.data() : null);
      },
      (err) => {
        if (err?.code === 'permission-denied') {
          console.warn(
            '[Firestore] users doc read denied — check Firestore rules for users/{userId} (see config/firestore-users.rules.fragment).',
          );
        } else {
          console.warn('[Firestore] users snapshot error:', err);
        }
        setUserData(null);
      },
    );
    return () => unsubscribe();
  }, [user]);

  const handleBuyCredits = () => {
    navigate('/buy-credits');
    setMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    setMobileMenuOpen(false);
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  // Safely check for user before accessing its email.
  const nameToDisplay = userData?.username || (user ? user.email : "Guest");
  const userCredits = userData?.credits ?? 0;

  return (
    <header className="header-container">
      <div className="logo">
        <a href="/">
          <img src={LOGO_SRC} alt="ShokIQ icon" className="header-logo-icon" />
        </a>
        <span className="beta-label" title={`ShokIQ web app v${APP_VERSION}`}>
          v{APP_VERSION}
        </span>
      </div>

      {/* Desktop version: visible only on larger screens */}
{/* Desktop version: visible only on larger screens */}
<div
      className="header-user-info desktop-only"
      style={{
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginRight: '10px',
        width: '100%',
        flexWrap: 'wrap',
        rowGap: '6px',
      }}
    >
  {user ? (
    <>
      {/* Tools Dropdown remains visible even for logged-in users */}
      <div className="tools-dropdown" style={{ position: 'relative', marginRight: '20px', width: 'auto' }}>
        <button style={styles.dropdownBtn}>
          Our Tools <FaAngleDown style={{ marginLeft: '5px' }} />
        </button>
        <div className="dropdown-content">
          <a href="http://www.chronocult.com" style={styles.dropdownLink}>⏱️ Easy Timer</a>
          <a href="/multi-dashboard" style={styles.dropdownLink}>📈 Video / Data </a>
          <a href="/video-sync-test" style={styles.dropdownLink}>🧪 Video Sync Test</a>
          <a href="/simple-test" style={styles.dropdownLink}>🔧 Simple Test</a>
        </div>
      </div>
      <button style={styles.buyCreditsBtn} onClick={handleBuyCredits}>
        Buy Credits <Tooltip textKey="buyCredits" className="tooltip-icon" />
      </button>
      <button style={styles.logoutBtn} onClick={handleSignOut}>
        Sign Out
      </button>
      <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#222', borderRadius: '5px', marginLeft: '10px', padding: '6px 12px' }} onClick={handleBuyCredits}>
        <span>{userCredits} ⚡</span>
      </div>
    </>
  ) : (
    <>
      <Link to="/pre-order" style={styles.guestNavLink}>
        Pre-order
      </Link>
      <Link to="/about" style={styles.guestNavLink}>
        About
      </Link>
      <Link to="/philosophy" style={styles.guestNavLink}>
        Tuning philosophy
      </Link>
      <Link to="/support" style={styles.guestNavLink}>
        Support
      </Link>
      <Link to="/privacypolicy" style={styles.guestNavLink}>
        Privacy
      </Link>
      <Link to="/tos" style={styles.guestNavLink} title="Terms of Service">
        TOS
      </Link>
      <button
        onClick={() => navigate("/login")}
        style={{
          padding: '6px 12px',
          backgroundColor: 'var(--brand-primary)',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          marginLeft: '8px',
        }}
      >
        Sign In
      </button>
    </>
  )}
</div>

      {/* Mobile version: hamburger menu icon (bright grey) */}
      <div className="mobile-only" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
        <button 
          onClick={() => setMobileMenuOpen(true)} 
          style={{ background: 'transparent', border: 'none', fontSize: '1.8em', cursor: 'pointer', color: '#ccc' }}
        >
          <FaBars />
        </button>
      </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay">
          <div className="mobile-menu">
            <button 
              onClick={() => setMobileMenuOpen(false)}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '1.5em',
                color: '#fff',
                position: 'absolute',
                top: '10px',
                right: '10px',
                cursor: 'pointer'
              }}
            >
              <FaTimes />
            </button>
            <div style={{ padding: '20px', color: '#fff', display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {user ? (
    <>
      <span style={{ display: 'block' }}>
        Hello, <strong>{nameToDisplay}</strong> | Credits: {userCredits}
        <Tooltip textKey="credits" />
      </span>
      <a href="/video-sync-test" style={{...styles.dropdownLink, color: '#fff', textDecoration: 'none', padding: '10px', backgroundColor: '#333', borderRadius: '4px'}}>
        🧪 Video Sync Test
      </a>
      <Link
        to="/support"
        onClick={() => setMobileMenuOpen(false)}
        style={{ color: '#fff', textDecoration: 'none', padding: '10px' }}
      >
        Support
      </Link>
      <Link
        to="/privacypolicy"
        onClick={() => setMobileMenuOpen(false)}
        style={{ color: '#fff', textDecoration: 'none', padding: '10px' }}
      >
        Privacy
      </Link>
      <Link
        to="/tos"
        onClick={() => setMobileMenuOpen(false)}
        style={{ color: '#fff', textDecoration: 'none', padding: '10px' }}
        title="Terms of Service"
      >
        TOS
      </Link>
      <button style={styles.buyCreditsBtn} onClick={handleBuyCredits}>
        Buy Credits
      </button>
      <button style={styles.logoutBtn} onClick={handleSignOut}>
        Sign Out
      </button>
    </>
  ) : (
    <>
      <Link
        to="/pre-order"
        onClick={() => setMobileMenuOpen(false)}
        style={{ color: '#fff', textDecoration: 'none', padding: '10px' }}
      >
        Pre-order
      </Link>
      <Link
        to="/about"
        onClick={() => setMobileMenuOpen(false)}
        style={{ color: '#fff', textDecoration: 'none', padding: '10px' }}
      >
        About
      </Link>
      <Link
        to="/philosophy"
        onClick={() => setMobileMenuOpen(false)}
        style={{ color: '#fff', textDecoration: 'none', padding: '10px' }}
      >
        Tuning philosophy
      </Link>
      <Link
        to="/support"
        onClick={() => setMobileMenuOpen(false)}
        style={{ color: '#fff', textDecoration: 'none', padding: '10px' }}
      >
        Support
      </Link>
      <Link
        to="/privacypolicy"
        onClick={() => setMobileMenuOpen(false)}
        style={{ color: '#fff', textDecoration: 'none', padding: '10px' }}
      >
        Privacy
      </Link>
      <Link
        to="/tos"
        onClick={() => setMobileMenuOpen(false)}
        style={{ color: '#fff', textDecoration: 'none', padding: '10px' }}
        title="Terms of Service"
      >
        TOS
      </Link>
      <button
        onClick={() => {
          setMobileMenuOpen(false);
          navigate('/login');
        }}
        style={{
          padding: '6px 12px',
          backgroundColor: 'var(--brand-primary)',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        Sign In
      </button>
    </>
  )}
              
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

const styles = {
  guestNavLink: {
    padding: '6px 10px',
    color: '#fff',
    textDecoration: 'none',
    fontSize: '0.9em',
  },
  buyCreditsBtn: {
    padding: '6px 12px',
    backgroundColor: 'var(--brand-primary)',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '10px',
  },
  logoutBtn: {
    padding: '6px 12px',
    backgroundColor: '#d9534f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  dropdownBtn: {
    padding: '6px 12px',
    backgroundColor: 'transparent',
    color: '#fff',
    border: '0px solid #fff',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9em'
  },
  dropdownLink: {
    display: 'block',
    padding: '8px 12px',
    textDecoration: 'none',
    color: '#ddd',
    fontSize: '0.9em'
  }
};

export default Header;